/**
 * Email Verification Service
 * 
 * This service handles all email verification related operations including:
 * - Sending verification emails
 * - Checking verification status
 * - Managing cooldown timers
 */

import firebase from "../config/firebase";
import { toast } from "react-toastify";

class EmailVerificationService {
  constructor() {
    this.cooldownKey = "emailVerificationCooldown";
    this.cooldownDuration = 60000; // 60 seconds in milliseconds
  }

  /**
   * Send verification email to the current user
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async sendVerificationEmail() {
    try {
      const user = firebase.auth().currentUser;
      
      if (!user) {
        return { success: false, error: "No user is currently signed in" };
      }

      if (user.emailVerified) {
        return { success: false, error: "Email is already verified" };
      }

      // Check cooldown
      const cooldownRemaining = this.getCooldownRemaining();
      if (cooldownRemaining > 0) {
        return {
          success: false,
          error: `Please wait ${Math.ceil(cooldownRemaining / 1000)} seconds before resending`,
        };
      }

      // Send verification email
      await user.sendEmailVerification({
        url: window.location.origin + "/auth/login", // Redirect URL after verification
        handleCodeInApp: false,
      });

      // Set cooldown
      this.setCooldown();

      return { success: true };
    } catch (error) {
      console.error("Error sending verification email:", error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Reload current user and check email verification status
   * @returns {Promise<{verified: boolean, error?: string}>}
   */
  async checkVerificationStatus() {
    try {
      const user = firebase.auth().currentUser;
      
      if (!user) {
        return { verified: false, error: "No user is currently signed in" };
      }

      // Reload user to get fresh data from Firebase
      await user.reload();
      
      // Get the updated user
      const updatedUser = firebase.auth().currentUser;
      
      return { verified: updatedUser.emailVerified };
    } catch (error) {
      console.error("Error checking verification status:", error);
      return { verified: false, error: error.message };
    }
  }

  /**
   * Set cooldown timestamp in localStorage
   */
  setCooldown() {
    const cooldownUntil = Date.now() + this.cooldownDuration;
    localStorage.setItem(this.cooldownKey, cooldownUntil.toString());
  }

  /**
   * Get remaining cooldown time in milliseconds
   * @returns {number} Remaining time in ms, or 0 if no cooldown
   */
  getCooldownRemaining() {
    const cooldownUntil = localStorage.getItem(this.cooldownKey);
    if (!cooldownUntil) return 0;

    const remaining = parseInt(cooldownUntil) - Date.now();
    return remaining > 0 ? remaining : 0;
  }

  /**
   * Clear cooldown (useful for testing or after successful verification)
   */
  clearCooldown() {
    localStorage.removeItem(this.cooldownKey);
  }

  /**
   * Get current user's email
   * @returns {string|null}
   */
  getCurrentUserEmail() {
    const user = firebase.auth().currentUser;
    return user ? user.email : null;
  }

  /**
   * Check if current user exists and is not verified
   * @returns {boolean}
   */
  isUserUnverified() {
    const user = firebase.auth().currentUser;
    return user && !user.emailVerified;
  }
}

export default new EmailVerificationService();
